import { Ban, CircleAlert, CreditCard, MinusCircle, ShieldX, TimerReset, WalletCards } from 'lucide-react'
import * as React from 'react'

import { Button } from '../components/button'
import { Loader } from '../components/loader'
import { ParsleyMark } from '../components/parsley-mark'
import { cn } from '../lib/utils'

// The content of `/subscribe/return` — the standalone page Stripe returns to after same-tab
// Checkout. Ten states, six treatments, three signal colours (#1254).
//
// ## This page is a receipt, not a step
//
// The return renders from NOTHING: a cold page load, no in-memory state, no knowledge a checkout
// was ever started, possibly a different browser than the user left from, and it replays from
// history. So there is no progress bar, no "step 4 of 4", no back-link, and no copy that refers to
// what the user just did — the page can't claim a journey it has no memory of. For the same reason
// every string is a standing fact ("your plan is active"), never an event ("your payment just went
// through"): a user hitting Back sees it again a week later.
//
// And it has to be verifiable rather than declarative. "You're all set" is a claim; a user who has
// just handed over a card wants the facts. The four states that have a billing snapshot render a
// facts strip — plan, card, next charge — which is what actually answers "did the payment go
// through?", because it stops being our word for it.

export type SubscribeReturnState =
  | 'working'
  | 'slow'
  | 'converted'
  | 'cardSaved'
  | 'declined'
  | 'setupIncomplete'
  | 'failed'
  | 'noSubscription'
  | 'sessionMismatch'
  | 'notConvertible'

export type SubscribeReturnActionId = 'kitchen' | 'subscription' | 'retry'

type Tone = 'quiet' | 'neutral' | 'good' | 'amber' | 'gray'
type MedTone = 'lift' | 'solid' | 'soft' | 'amber' | 'mute'
type Glyph = 'loader' | 'clock' | 'check' | 'card' | 'cardOff' | 'cardDash' | 'alert' | 'minus' | 'shieldX' | 'ban'

interface StateConfig {
  tone: Tone
  glyph: Glyph
  medTone: MedTone
  /** Announce as busy — the wait states only. */
  busy?: boolean
  /** The leaf watermark. `converted` alone gets it; it's the celebration. */
  leaf?: boolean
  /** "Payments and receipts are handled by Stripe." Absent on dead ends — no payment happened. */
  fine?: boolean
  actions: Array<[id: SubscribeReturnActionId, variant: 'default' | 'ghost' | 'secondary']>
}

/**
 * Tone, glyph and actions are derived from `state`, never passed in — the same enforcement as
 * `SUBSCRIPTION_CARD_STATES` and `BB_STATES`. A caller can't accidentally give `declined` the green
 * card, and adding an eleventh state means picking a treatment rather than designing a screen.
 *
 * Six treatments across ten states. The grouping question is *what must the user understand, and
 * what can they do about it* — not *what did Stripe return*:
 *
 * - **waiting** — `working`, `slow`. No signal colour; nothing has failed.
 * - **converted** — the only green surface and the only leaf.
 * - **card saved** — deliberately NOT the green card. See below.
 * - **needs your card** — `declined`. The only amber in the flow.
 * - **try again** — `setupIncomplete`, `failed`. Different causes, identical card; the copy carries
 *   the difference, and `failed` gets a second exit because "Try again" might fail again.
 * - **nothing to do here** — the three dead ends. One treatment, three copy decks, one exit.
 */
export const RETURN_STATES: Record<SubscribeReturnState, StateConfig> = {
  working: { tone: 'quiet', glyph: 'loader', medTone: 'lift', busy: true, fine: true, actions: [] },
  slow: {
    tone: 'quiet',
    glyph: 'clock',
    medTone: 'lift',
    busy: true,
    fine: true,
    actions: [
      ['subscription', 'default'],
      ['kitchen', 'ghost']
    ]
  },
  converted: { tone: 'good', glyph: 'check', medTone: 'solid', leaf: true, fine: true, actions: [['kitchen', 'default']] },
  // Not the green card, on purpose. This state once shipped rendering `converted`'s copy — "your
  // plan is active" — which was false and was contradicted by the settings page seconds later.
  // Nothing has been charged and the trial is still running, so the ramp has to carry that too:
  // a plain card and a SOFT green medallion. Good, one clear step below celebration. Same
  // hierarchy as SubscriptionStatusCard's hero vs hero-lite, where `trialing` must not read
  // as `active`.
  cardSaved: { tone: 'neutral', glyph: 'card', medTone: 'soft', fine: true, actions: [['kitchen', 'default']] },
  // The only amber, and never red — red is reserved for destructive user actions (BillingBanner,
  // #1252). The subscription exists, the card is saved, 14 days of full access are running: this
  // is a refusal about a card, not about the account. Hence a card-with-a-line-through medallion
  // rather than an alert, and the word "failed" nowhere in the copy.
  declined: {
    tone: 'amber',
    glyph: 'cardOff',
    medTone: 'amber',
    fine: true,
    actions: [
      ['subscription', 'default'],
      ['kitchen', 'ghost']
    ]
  },
  setupIncomplete: { tone: 'neutral', glyph: 'cardDash', medTone: 'mute', fine: true, actions: [['retry', 'default']] },
  failed: {
    tone: 'neutral',
    glyph: 'alert',
    medTone: 'mute',
    fine: true,
    actions: [
      ['retry', 'default'],
      ['subscription', 'ghost']
    ]
  },
  // The three dead ends get the quietest surface, NO amber and NO primary button — because there
  // is nothing here to fix. That absence is the design: an amber CTA on a dead end implies a
  // repair that doesn't exist.
  noSubscription: { tone: 'gray', glyph: 'minus', medTone: 'mute', actions: [['subscription', 'secondary']] },
  sessionMismatch: { tone: 'gray', glyph: 'shieldX', medTone: 'mute', actions: [['subscription', 'secondary']] },
  notConvertible: { tone: 'gray', glyph: 'ban', medTone: 'mute', actions: [['subscription', 'secondary']] }
}

/** Identical construction to `SubscriptionStatusCard`'s slab fills, so the sibling pages match. */
const SURFACE: Record<Tone, string> = {
  quiet: 'bg-card-sunken',
  neutral: 'bg-card',
  good: cn(
    'bg-[color-mix(in_oklab,oklch(var(--brand-green-500))_11%,oklch(var(--card)))] text-brand-green-800',
    'dark:bg-[color-mix(in_oklab,oklch(var(--brand-green-700))_34%,oklch(var(--card)))] dark:text-brand-green-200'
  ),
  // `amber-800`, not `text-amber-strong` — that measures 3.22:1 on an amber wash and fails AA in
  // light mode. Same override BillingBanner carries; both disappear when #1214 lands an ink role.
  amber: cn(
    'bg-[color-mix(in_oklab,oklch(var(--amber-500))_14%,oklch(var(--card)))] text-amber-800',
    'dark:bg-[color-mix(in_oklab,oklch(var(--amber-500))_22%,oklch(var(--card)))] dark:text-amber-200'
  ),
  gray: cn(
    'bg-[color-mix(in_oklab,oklch(var(--gray-400))_16%,oklch(var(--card)))] text-foreground',
    'dark:bg-[color-mix(in_oklab,oklch(var(--gray-600))_26%,oklch(var(--card)))]'
  )
}

/** The `good` headline steps one shade past its body ink; every other tone inherits. */
const HEAD_INK: Partial<Record<Tone, string>> = {
  good: 'text-brand-green-900 dark:text-brand-green-100'
}

const MED_TILE: Record<MedTone, string> = {
  solid: 'bg-brand-green-700 text-white',
  soft: cn(
    'bg-[color-mix(in_oklab,oklch(var(--brand-green-500))_22%,oklch(var(--card)))] text-brand-green-800',
    'dark:bg-[color-mix(in_oklab,oklch(var(--brand-green-700))_48%,oklch(var(--card)))] dark:text-brand-green-100'
  ),
  amber: cn(
    'bg-[color-mix(in_oklab,oklch(var(--amber-500))_26%,oklch(var(--card)))] text-amber-800',
    'dark:bg-[color-mix(in_oklab,oklch(var(--amber-500))_30%,oklch(var(--card)))] dark:text-amber-200'
  ),
  lift: cn(
    'bg-card text-brand-green-700',
    'dark:bg-[color-mix(in_oklab,oklch(var(--foreground))_9%,oklch(var(--card)))] dark:text-brand-green-300'
  ),
  mute: cn(
    'bg-[color-mix(in_oklab,oklch(var(--foreground))_8%,oklch(var(--card)))] text-gray-700',
    'dark:bg-[color-mix(in_oklab,oklch(var(--foreground))_13%,oklch(var(--card)))] dark:text-gray-200'
  )
}

type GlyphProps = { className?: string; strokeWidth?: string | number }

const GLYPH: Record<Exclude<Glyph, 'loader'>, React.ComponentType<GlyphProps>> = {
  clock: TimerReset,
  check: CheckGlyph,
  card: WalletCards,
  cardOff: CardOff,
  cardDash: CardDash,
  alert: CircleAlert,
  minus: MinusCircle,
  shieldX: ShieldX,
  ban: Ban
}

export interface SubscribeReturnCopy {
  head: string
  body: string
  /** `working` only — "This finishes on its own. It's safe to close this page." */
  permission?: string
  /** `working` only — the label for the delayed escape link. */
  escape?: string
  /** Pre-formatted pairs. The card does no date or currency maths. */
  facts?: Array<[label: string, value: string]>
}

export interface SubscribeReturnCardProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  state: SubscribeReturnState
  /** Already translated — `@parsleyai/ui` is i18n-free. */
  copy: SubscribeReturnCopy
  /** Reveal the escape link. The route owns the timer; the card owns the fade. */
  showEscape?: boolean
  /** Fine print under the card. Rendered only where {@link RETURN_STATES} says it belongs. */
  finePrint?: string
  /** Called with the action's id — destinations are an app concern. */
  onAction?: (id: SubscribeReturnActionId) => void
  /** Labels for the three exits, already translated. */
  actionLabels: Record<SubscribeReturnActionId, string>
}

export const SubscribeReturnCard = React.forwardRef<HTMLDivElement, SubscribeReturnCardProps>(
  ({ state, copy, showEscape, finePrint, onAction, actionLabels, className, ...props }, ref) => {
    const cfg = RETURN_STATES[state]
    const facts = copy.facts?.length ? copy.facts : null

    return (
      <div ref={ref} className={cn('flex w-full max-w-[432px] flex-col items-stretch sm:max-w-[472px]', className)} {...props}>
        <div
          // `status` + `polite`: the state changes under the user without any interaction, so a
          // screen reader has to hear it. `busy` only while genuinely waiting.
          role="status"
          aria-live="polite"
          aria-busy={cfg.busy || undefined}
          className={cn('relative overflow-hidden rounded-[22px] px-[26px] py-6 shadow-card sm:px-[34px] sm:py-[34px]', SURFACE[cfg.tone])}>
          {cfg.leaf && (
            <span
              aria-hidden
              className="pointer-events-none absolute -right-6 -top-8 h-[150px] w-[168px] text-brand-green-500/[0.14] dark:text-brand-green-300/[0.10]">
              <ParsleyMark filled className="h-full w-full" />
            </span>
          )}

          <div className="relative flex flex-col items-start">
            <span className={cn('mb-5 grid size-[54px] place-items-center rounded-[17px]', MED_TILE[cfg.medTone])}>
              {cfg.glyph === 'loader' ? (
                <Loader variant="dots" size={26} aria-label={copy.head} />
              ) : (
                React.createElement(GLYPH[cfg.glyph], { className: 'size-[25px]', strokeWidth: 1.9 })
              )}
            </span>

            <h1
              className={cn(
                'font-brand font-semibold text-[26px] leading-[1.16] tracking-[-0.018em] sm:text-[29px]',
                '[text-wrap:balance]',
                HEAD_INK[cfg.tone]
              )}>
              {copy.head}
            </h1>

            <p className="mt-2 max-w-[42ch] text-[15px] leading-[1.55] opacity-90 [text-wrap:pretty]">{copy.body}</p>

            {copy.permission && (
              // The real fear during the wait isn't the delay, it's "if I touch anything, do I lose
              // my money?". Only copy can answer that; a button can't. And it's true — the work is
              // server-side, the client is just following a snapshot.
              <p className="mt-3 flex items-start gap-2 text-[13px] text-muted-foreground leading-[1.5]">
                <CircleAlert aria-hidden className="mt-px size-[15px] shrink-0" strokeWidth={1.9} />
                <span>{copy.permission}</span>
              </p>
            )}

            {facts && (
              // The single biggest addition of the design pass: what turns "you're all set" from a
              // claim into something the user can check.
              <dl className="mt-5 grid w-full grid-cols-[auto_1fr] gap-x-4 gap-y-[9px] rounded-[14px] bg-[color-mix(in_oklab,oklch(var(--foreground))_5%,transparent)] px-[15px] py-[13px] dark:bg-[color-mix(in_oklab,oklch(var(--foreground))_8%,transparent)]">
                {facts.map(([label, value]) => (
                  <React.Fragment key={label}>
                    <dt className="text-[12.5px] opacity-70">{label}</dt>
                    <dd className="text-right font-mono text-[13.5px] font-medium tabular-nums">{value}</dd>
                  </React.Fragment>
                ))}
              </dl>
            )}

            {cfg.actions.length > 0 && (
              // Full-width 44px on a phone, inline 36px from sm up. A deliberate divergence from
              // the DS default, with the same reasoning as BillingBanner's: these are the PAGE's
              // primary actions on a phone and there is nothing else on screen to touch.
              <div className="mt-6 flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center sm:gap-3">
                {cfg.actions.map(([id, variant]) => (
                  <Button key={id} variant={variant} onClick={() => onAction?.(id)} className="h-11 w-full sm:h-9 sm:w-auto">
                    {actionLabels[id]}
                  </Button>
                ))}
              </div>
            )}

            {copy.escape && (
              // A text link, not a button — safe for exactly the reason a button isn't: the work is
              // already server-side, so leaving can't interrupt a payment, and the destination reads
              // the SAME snapshot this page is waiting on. Most users never see it.
              <Button
                variant="link"
                onClick={() => onAction?.('subscription')}
                className={cn(
                  'mt-4 h-auto p-0 text-[13px] transition-opacity duration-500 motion-reduce:transition-none',
                  showEscape ? 'opacity-100' : 'pointer-events-none opacity-0'
                )}
                tabIndex={showEscape ? undefined : -1}
                aria-hidden={!showEscape}>
                {copy.escape}
              </Button>
            )}
          </div>
        </div>

        {cfg.fine && finePrint && <p className="mx-auto mt-4 max-w-[40ch] text-center text-[12.5px] text-muted-foreground">{finePrint}</p>}
      </div>
    )
  }
)
SubscribeReturnCard.displayName = 'SubscribeReturnCard'

/** A check that reads as a mark rather than a tick in a box — lucide's Check is too thin at 25px. */
function CheckGlyph({ className, strokeWidth = 1.9 }: GlyphProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={Number(strokeWidth) + 0.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden>
      <path d="M20 6 9 17l-5-5" />
    </svg>
  )
}

/** A card with a line through it — a refusal about a card, deliberately not an alert. */
function CardOff({ className, strokeWidth = 1.9 }: GlyphProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden>
      <rect width="20" height="14" x="2" y="5" rx="2" />
      <path d="M2 10h20" />
      <path d="m3 21 18-18" />
    </svg>
  )
}

/** A dashed card — nothing was captured, as opposed to something being wrong. */
function CardDash({ className, strokeWidth = 1.9 }: GlyphProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden>
      <rect width="20" height="14" x="2" y="5" rx="2" strokeDasharray="3 3" />
      <path d="M2 10h20" strokeDasharray="3 3" />
    </svg>
  )
}

// Referenced by the glyph table but re-exported for stories that want the raw icon.
export { CreditCard }
